import type { EChartsOption } from 'echarts';
import type { ChartData, TopItemsData } from '@/entities/dashboard';

import { resolveSeriesColors, sortChartDataByDate } from './chart-data';

const formatTooltipValue = (value: unknown): string => {
  const numeric = Number(value);

  return Number.isFinite(numeric) ? numeric.toLocaleString('en-US', { maximumFractionDigits: 1 }) : String(value ?? '');
};

// Tooltip formatters return HTML that ECharts injects into the DOM, so any
// API-sourced text (category, series name, unit) must be escaped.
const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const AXIS_LABEL_FONT_SIZE = 11;
const AXIS_LABEL_LINE_HEIGHT = 15;
const AXIS_LABEL_ROTATION = 45;
/**
 * Mean glyph width at `AXIS_LABEL_FONT_SIZE`, mixed-case Latin, in px — about
 * half the font size, the same ratio zrender's own width table averages to.
 */
const AXIS_LABEL_CHAR_WIDTH = 5.5;
/** Breathing room between one label and the next, px. */
const AXIS_LABEL_GAP = 4;
/**
 * Label box for an axis that is not clipping anything — wide enough to hold any
 * category, and present only so that a later state cannot leave its own, much
 * narrower, width behind in the merged option.
 */
const UNCONSTRAINED_LABEL_WIDTH = 1000;
/** Narrowest slot still worth wrapping a label into; below it the axis tilts. */
const WRAPPED_LABEL_MIN_SLOT = 55;
const WRAPPED_LABEL_MAX_LINES = 3;
/** Share of a wrapped line the text actually reaches before the next word wraps. */
const WRAPPED_LINE_FILL = 0.85;
/**
 * Marks a clipped label, in both the state ECharts clips for us and the one we
 * clip ourselves — ECharts' own default is three dots, which would have made the
 * two states disagree.
 */
const ELLIPSIS = '…';
/** Legacy RITA capped a tilted label at a third of the chart height. */
const TILTED_LABEL_HEIGHT_SHARE = 1 / 3;
/** …and a narrow chart cannot spare that, so the container caps it too. */
const TILTED_LABEL_WIDTH_SHARE = 0.25;
/** Share of the container a horizontal bar chart gives its category names. */
const BAR_LABEL_WIDTH_SHARE = 0.35;
const DEFAULT_GRID_LEFT = '3%';
const GRID_RIGHT = '4%';
/** Grid insets plus the y-axis labels, as a share of the container and px. */
const PLOT_WIDTH_SHARE = 0.93;
const Y_AXIS_LABEL_ALLOWANCE = 45;

export interface ChartSize {
  width: number;
  height: number;
}

export interface ChartViewport extends ChartSize {
  /**
   * Share of the categories the dataZoom window is showing, 0–1. Zooming in
   * hands the remaining categories more room each, which is as much a change of
   * available space as resizing the chart is.
   */
  visibleShare: number;
}

/** Stand-in until the container reports its size — see `useContainerSize`. */
const UNMEASURED: ChartViewport = { width: 0, height: 0, visibleShare: 1 };

const getResponsiveChartConfig = () => {
  const isMobile = window.innerWidth < 768;

  return {
    legend: {
      bottom: isMobile ? 45 : 0,
      type: 'scroll',
      pageIconSize: isMobile ? 12 : 15
    },
    grid: {
      bottom: isMobile ? '20%' : '18%',
      top: isMobile ? '18%' : '15%'
    },
    dataZoom: {
      bottom: isMobile ? 10 : 40,
      height: isMobile ? 15 : 20
    }
  };
};

const estimateLabelWidth = (text: string): number => text.length * AXIS_LABEL_CHAR_WIDTH;

const keepWholeLabel = (value: string): string => value;

/** Greedy word wrap, using the estimate the layout decisions are taken on. */
const wrapLabel = (value: string, width: number): string[] => {
  const lines: string[] = [];
  let line = '';

  for (const word of value.split(/\s+/).filter(Boolean)) {
    const candidate = line ? `${line} ${word}` : word;

    if (!line || estimateLabelWidth(candidate) <= width) {
      line = candidate;
    } else {
      lines.push(line);
      line = word;
    }
  }

  if (line) {
    lines.push(line);
  }

  return lines;
};

/**
 * Cuts a label down to what its wrapped box can show, ending in an ellipsis.
 *
 * ECharts marks nothing when `lineOverflow` clamps a label: the text simply
 * stops mid-word, so "Christian Recovery and Development Agency" reads as an
 * organisation called "Christian Recovery and Developme". Only single-line
 * `overflow: 'truncate'` — what the tilted state uses — draws an ellipsis of its
 * own, so the wrapped state has to cut its own text.
 *
 * The lines are handed back with the breaks in them, so that where the text ends
 * and where the ellipsis sits are decided once, here, rather than measured one
 * way here and re-wrapped another way by ECharts. Wrapping happens against a
 * slightly narrower box than the real one (`WRAPPED_LINE_FILL`), so a name whose
 * glyphs run wider than the estimate cannot push its own ellipsis onto a fourth
 * line and back out of sight.
 */
const clampWrappedLabel = (value: string, width: number): string => {
  const lineWidth = width * WRAPPED_LINE_FILL;
  const lines = wrapLabel(value, lineWidth);

  if (lines.length <= WRAPPED_LABEL_MAX_LINES) {
    return value;
  }

  const kept = lines.slice(0, WRAPPED_LABEL_MAX_LINES);
  // The last line carries as much of the rest of the name as fits, so it reads
  // "and Devel…" rather than the "and…" a clean word break would leave.
  let last = [kept.pop(), ...lines.slice(WRAPPED_LABEL_MAX_LINES)].join(' ');

  while (last.length > 1 && estimateLabelWidth(`${last}${ELLIPSIS}`) > lineWidth) {
    last = last.slice(0, -1).trimEnd();
  }

  return [...kept, `${last}${ELLIPSIS}`].join('\n');
};

/**
 * X-axis labels for charts whose categories come from the *Group by* filter,
 * plus the grid inset they need.
 *
 * Categories are free text from the API — organisation and location names run to
 * 40 characters, dates are short but there can be hundreds of them — so what
 * matters is not the text but how much room a single category gets. Legacy RITA
 * (Highcharts) took the same measurement and landed in one of three states,
 * which this reproduces:
 *
 * 1. the longest label fits its slot → upright, one line;
 * 2. it does not, but the slot is still wide enough to read a wrapped column →
 *    upright, wrapped, clipped after `WRAPPED_LABEL_MAX_LINES`;
 * 3. the slot is narrower than that → tilted 45° and clipped by width.
 *
 * Date and text categories go through the same rule: legacy tilted a year of
 * daily ticks and left a month of them upright, and so does this.
 *
 * Every category stays a candidate (`interval: 0`) and `hideOverlap` drops the
 * ones that would still collide, rather than letting them overlap. The tooltip
 * keeps the untruncated name in all three states.
 *
 * `size` is `{ 0, 0 }` until the container is measured; that reads as state 1,
 * the cheapest thing to correct once the real size arrives.
 */
const createCategoryAxisLayout = (categories: string[], viewport: ChartViewport) => {
  /**
   * Every state fills in every one of these keys. `setOption` merges by default,
   * so a key one state leaves out keeps whatever the previous state put there —
   * which is how an axis that had tilted stayed tilted after growing wide enough
   * to sit upright, and how a wrapped axis dragged its line clamp into the
   * tilted state.
   */
  const uprightLabel = (width: number) => ({
    interval: 0,
    fontSize: AXIS_LABEL_FONT_SIZE,
    hideOverlap: true,
    rotate: 0,
    width,
    height: AXIS_LABEL_LINE_HEIGHT,
    // Pinned rather than left to the font: `height` is what clamps the wrapped
    // state, and the character budget behind its ellipsis counts the same lines.
    lineHeight: AXIS_LABEL_LINE_HEIGHT,
    overflow: 'none' as const,
    lineOverflow: 'truncate' as const,
    ellipsis: ELLIPSIS,
    formatter: keepWholeLabel
  });

  // The slot is measured against the upright grid; a tilted axis insets the plot
  // further, which only makes the slot the decision was taken on conservative.
  const plotWidth = viewport.width * PLOT_WIDTH_SHARE - Y_AXIS_LABEL_ALLOWANCE;

  if (!categories.length || plotWidth <= 0) {
    return { gridLeft: DEFAULT_GRID_LEFT, axisLabel: uprightLabel(UNCONSTRAINED_LABEL_WIDTH) };
  }

  // Only the categories inside the dataZoom window compete for the plot. An
  // unusable share reads as "all of them" rather than poisoning the slot with a
  // NaN, which would silently drop every axis into the tilted branch.
  const share = Number.isFinite(viewport.visibleShare) ? Math.min(Math.max(viewport.visibleShare, 0), 1) : 1;
  const visibleCategories = Math.max(1, Math.round(categories.length * share));
  const slot = plotWidth / visibleCategories;
  const longestLabel = categories.reduce((widest, category) => Math.max(widest, estimateLabelWidth(category)), 0);

  if (longestLabel <= slot - AXIS_LABEL_GAP) {
    return { gridLeft: DEFAULT_GRID_LEFT, axisLabel: uprightLabel(UNCONSTRAINED_LABEL_WIDTH) };
  }

  // Wrapping is only worth it while whole words still fit: a slot narrower than
  // the longest word leaves ECharts breaking words down the middle, which reads
  // as "Internati / onal / Rescue" and takes the ellipsis with it, because the
  // broken halves push the clipped line out of the clamp. Legacy tilted such an
  // axis rather than break its words, and so do we.
  const longestWord = categories.reduce(
    (widest, category) =>
      category.split(/\s+/).reduce((wordWidth, word) => Math.max(wordWidth, estimateLabelWidth(word)), widest),
    0
  );

  if (slot >= WRAPPED_LABEL_MIN_SLOT && longestWord <= slot * WRAPPED_LINE_FILL) {
    // The whole slot, not slot-minus-gap: wrapped text is centred in its box and
    // neighbours only touch.
    const boxWidth = Math.floor(slot);

    return {
      gridLeft: DEFAULT_GRID_LEFT,
      axisLabel: {
        ...uprightLabel(boxWidth),
        overflow: 'break' as const,
        height: WRAPPED_LABEL_MAX_LINES * AXIS_LABEL_LINE_HEIGHT,
        // One slot-wide box per category tiles with its neighbours instead of
        // overlapping them, so nothing has to be dropped to keep the axis legible.
        hideOverlap: false,
        formatter: (value: string) => clampWrappedLabel(value, boxWidth)
      }
    };
  }

  const maxWidth = Math.round(
    Math.min(viewport.height * TILTED_LABEL_HEIGHT_SHARE, viewport.width * TILTED_LABEL_WIDTH_SHARE)
  );

  return {
    // A tilted label trails down and to the left of its own tick, and
    // `containLabel` only reserves room below the axis. Without this inset the
    // leading labels are sliced off by the canvas edge as categories crowd
    // together and their ticks march left.
    gridLeft: Math.round(Math.cos((AXIS_LABEL_ROTATION * Math.PI) / 180) * maxWidth),
    axisLabel: {
      ...uprightLabel(maxWidth),
      rotate: AXIS_LABEL_ROTATION,
      overflow: 'truncate' as const
    }
  };
};

export const createAreaChartOption = (rawData: ChartData, viewport: ChartViewport = UNMEASURED): EChartsOption => {
  const data = sortChartDataByDate(rawData);
  const seriesColors = resolveSeriesColors(data.series);
  const responsive = getResponsiveChartConfig();
  const categoryAxis = createCategoryAxisLayout(data.categories, viewport);

  return {
    title: {
      text: data.title,
      left: 'center',
      textStyle: {
        fontSize: 16,
        fontWeight: 'normal'
      }
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        label: {
          backgroundColor: '#6a7985'
        }
      },
      formatter: (params) => {
        if (Array.isArray(params)) {
          let result = `<div><strong>${escapeHtml(params[0].name)}</strong></div>`;
          params.forEach((param) => {
            const value = formatTooltipValue(param.value);
            result += `<div>${param.marker} ${escapeHtml(param.seriesName ?? '')}: <strong>${escapeHtml(value)}${escapeHtml(data.unitLabel || '')}</strong></div>`;
          });
          return result;
        }
        return '';
      }
    },
    legend: {
      data: data.series.map((s) => s.name),
      ...responsive.legend
    },
    grid: {
      left: categoryAxis.gridLeft,
      right: GRID_RIGHT,
      ...responsive.grid,
      containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: data.categories,
      axisLabel: categoryAxis.axisLabel
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: (value: number) => {
          if (value >= 1000) {
            return (value / 1000).toFixed(1) + 'k';
          }
          return value.toString();
        }
      }
    },
    dataZoom: [
      {
        type: 'inside',
        start: 0,
        end: 100,
        xAxisIndex: 0
      },
      {
        type: 'slider',
        show: true,
        start: 0,
        end: 100,
        bottom: responsive.dataZoom.bottom,
        height: responsive.dataZoom.height,
        borderColor: 'transparent',
        backgroundColor: '#f4f4f4',
        fillerColor: 'rgba(144, 147, 153, 0.3)',
        handleStyle: {
          color: '#6190e8',
          borderWidth: 0
        },
        moveHandleStyle: {
          color: '#6190e8'
        }
      }
    ],
    series: data.series.map((series, index) => ({
      name: series.name,
      type: 'line',
      stack: series.stack || 'Total',
      areaStyle: {
        opacity: 0.6
      },
      emphasis: {
        focus: 'series'
      },
      data: series.data,
      itemStyle: seriesColors[index] ? { color: seriesColors[index] } : undefined,
      lineStyle: {
        width: 2
      },
      smooth: true
    }))
  };
};

export const createColumnChartOption = (rawData: ChartData, viewport: ChartViewport = UNMEASURED): EChartsOption => {
  const data = sortChartDataByDate(rawData);
  const seriesColors = resolveSeriesColors(data.series);
  const responsive = getResponsiveChartConfig();
  const categoryAxis = createCategoryAxisLayout(data.categories, viewport);

  return {
    title: {
      text: data.title,
      left: 'center',
      textStyle: {
        fontSize: 16,
        fontWeight: 'normal'
      }
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      formatter: (params) => {
        if (Array.isArray(params)) {
          let result = `<div><strong>${escapeHtml(params[0].name)}</strong></div>`;
          params.forEach((param) => {
            const value = formatTooltipValue(param.value);
            result += `<div>${param.marker} ${escapeHtml(param.seriesName ?? '')}: <strong>${escapeHtml(value)}${escapeHtml(data.unitLabel || '')}</strong></div>`;
          });
          return result;
        }
        return '';
      }
    },
    legend: {
      data: data.series.map((s) => s.name),
      ...responsive.legend
    },
    grid: {
      left: categoryAxis.gridLeft,
      right: GRID_RIGHT,
      ...responsive.grid,
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: data.categories,
      axisLabel: categoryAxis.axisLabel
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: (value: number) => {
          if (value >= 1000) {
            return (value / 1000).toFixed(1) + 'k';
          }
          return value.toString();
        }
      }
    },
    dataZoom: [
      {
        type: 'inside',
        start: 0,
        end: 100,
        xAxisIndex: 0
      },
      {
        type: 'slider',
        show: true,
        start: 0,
        end: 100,
        bottom: responsive.dataZoom.bottom,
        height: responsive.dataZoom.height,
        borderColor: 'transparent',
        backgroundColor: '#f4f4f4',
        fillerColor: 'rgba(144, 147, 153, 0.3)',
        handleStyle: {
          color: '#6190e8',
          borderWidth: 0
        },
        moveHandleStyle: {
          color: '#6190e8'
        }
      }
    ],
    series: data.series.map((series, index) => ({
      name: series.name,
      type: 'bar',
      stack: series.stack,
      data: series.data,
      itemStyle: seriesColors[index] ? { color: seriesColors[index] } : undefined
    }))
  };
};

export const createBarChartOption = (data: TopItemsData, viewport: ChartViewport = UNMEASURED): EChartsOption => {
  const isMobile = window.innerWidth < 768;

  return {
    tooltip: {
      trigger: 'item',
      formatter: '{b}: <strong>{c}%</strong>'
    },
    grid: {
      left: isMobile ? '5%' : '3%',
      right: isMobile ? '5%' : '4%',
      bottom: '3%',
      top: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'value',
      max: 100,
      axisLabel: {
        formatter: '{value}%'
      }
    },
    yAxis: {
      type: 'category',
      data: data.series.map((s) => s.name),
      inverse: true,
      axisLabel: {
        interval: 0,
        formatter: '{value}',
        // Warehouse and organisation names are long enough to run off the card,
        // and a horizontal bar has no slot to tilt into — it can only clip. The
        // tooltip still carries the full name.
        ...(viewport.width
          ? { width: Math.round(viewport.width * BAR_LABEL_WIDTH_SHARE), overflow: 'truncate' as const }
          : {})
      }
    },
    series: [
      {
        type: 'bar',
        data: data.series.map((s) => ({
          value: s.percentage,
          itemStyle: { color: s.color }
        })),
        barWidth: '60%'
      }
    ]
  };
};

export const createPieChartOption = (data: TopItemsData, title?: string): EChartsOption => {
  const isMobile = window.innerWidth < 768;

  return {
    title: title
      ? {
          text: title,
          left: 'center',
          textStyle: {
            fontSize: isMobile ? 14 : 16,
            fontWeight: 'normal'
          }
        }
      : undefined,
    tooltip: {
      trigger: 'item',
      formatter: '{b}: <strong>{c}%</strong>'
    },
    legend: {
      orient: isMobile ? 'horizontal' : 'vertical',
      left: isMobile ? 'center' : 'left',
      bottom: isMobile ? 0 : 'auto',
      type: isMobile ? 'scroll' : 'plain'
    },
    series: [
      {
        type: 'pie',
        radius: isMobile ? '40%' : '50%',
        center: isMobile ? ['50%', '45%'] : ['50%', '50%'],
        data: data.series.map((s) => ({
          value: s.percentage,
          name: s.name,
          itemStyle: { color: s.color }
        })),
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        }
      }
    ]
  };
};
